/* eslint-disable max-lines */
// constants
import {
    CLICK_LABELS,
    DO_CLICK_ACTIONS,
    DO_IMPRESSION_ACTIONS,
    IMPRESSION_LABELS,
    LABELS,
    SCREEN_NAME,
    SOURCE,
} from "@dineout-payment-success-ui/analytics/constants";

// internal-deps
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";

// interfaces
import {
    IEventContext,
    IImpressionBillDetailsDineoutBookingSuccess,
    IImpressionBillDetailsEvent,
    IClickCancelBookingCta,
    IClickBookingTicketCta,
    IClickMapsCTA,
    IClickCancelBookingPopupCta,
    IClickCallRestaurantCTA,
    IClickTnCDineoutBookingSuccess,
    IClickViewRestaurantDetails,
    IImpressionBenefitCallout,
    IClickThingsToRemember,
    IImpressionYourBookings,
    IImpressionBookingCard,
    IImpressionCashBackBenefitCallout,
} from "@dineout-payment-success-ui/interfaces/AnalyticsEvents";

export const clickDoneDineoutBookingSuccess = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    orderId: string,
    dealCategory: string,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        value: LABELS.DONE_CLICK,
        context: JSON.stringify({
            orderId,
            dealCategory,
        }),
    });
};

export const clickViewHelpDineoutBookingSuccess = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    orderId: string,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.HELP : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: LABELS.HELP_CLICK,
        context: JSON.stringify({
            orderId,
            source: SOURCE,
        }),
    });
};

export const impressionDinecashPoolBanner = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { orderId, restaurantId }: { orderId: string; restaurantId: string },
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.DINECASH_POOL_BANNER,
        context: JSON.stringify({
            orderId,
            restaurantId,
        }),
    });
};

export const impressionDineoutBookingSuccess = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    isCheckoutFlow: boolean,
    { orderId, bookingStatus }: { orderId: string; bookingStatus: string },
): void => {
    impressionEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: DO_IMPRESSION_ACTIONS.BOOKING_SUCCESS,
        context: JSON.stringify({
            orderId,
            bookingStatus,
        }),
    });
};

export const impressionCashBackBenefitCallout = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { bannerText, dealCategory, restaurantId }: IImpressionCashBackBenefitCallout,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.CASHBACK_BENEFIT_CALLOUT,
        label: IMPRESSION_LABELS.CASHBACK_BENEFIT_CALLOUT,
        context: JSON.stringify({ bannerText, dealCategory, restaurantId }),
    });
};

export const impressionBillDetailsDineoutBookingSuccess = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    isCheckoutFlow: boolean,
    { dealCategory, restaurantID, guests, billDetails }: IImpressionBillDetailsDineoutBookingSuccess,
): void => {
    impressionEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: DO_IMPRESSION_ACTIONS.BILL_DETAILS,
        context: JSON.stringify({
            restaurantID,
            dealCategory,
            guests,
            billDetails,
            source: SOURCE,
        }),
    });
};

export const impressionDownloadCta = (impressionEvent: AnalyticsContextValue["impressionEvent"]): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKING_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.DOWNLOAD_CTA,
    });
};

export const clickDownloadCta = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKING_SUCCESS,
        action: DO_CLICK_ACTIONS.DOWNLOAD_CTA,
    });
};

export const clickShareIcon = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId, restaurantId, eventId, dealCategory, guests }: IEventContext,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: DO_CLICK_ACTIONS.TABLE_RESERVED,
        label: CLICK_LABELS.SHARE_CLICK,
        context: JSON.stringify({
            restaurantId,
            orderId,
            eventId,
            guests,
            dealCategory,
        }),
    });
};

export const impressionBillDetailsEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { dealCategory, restaurantID, eventId, guests, billDetails, ticketDetails }: IImpressionBillDetailsEvent,
): void => {
    impressionEvent({
        category: SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: DO_IMPRESSION_ACTIONS.BOOKING_DETAILS,
        label: IMPRESSION_LABELS.BILL_DETAILS,
        context: JSON.stringify({
            dealCategory,
            restaurantID: String(restaurantID),
            eventId,
            guests,
            billDetails,
            ticketDetails,
        }),
    });
};

export const clickCancelBookingCta = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId, dealCategory }: IClickCancelBookingCta,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.CANCEL_BOOKING_CTA_CLICK,
        context: JSON.stringify({
            orderId,
            dealCategory,
        }),
    });
};

export const clickBookingTicketCta = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { ctaTitle, ctaDeeplink, orderId, dealCategory }: IClickBookingTicketCta,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.BOOKING_TICKET_CTA_CLICK,
        context: JSON.stringify({
            ctaTitle,
            ctaDeeplink,
            orderId,
            dealCategory,
        }),
    });
};

export const clickMapsCTA = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId, dealCategory }: IClickMapsCTA,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.MAPS_CLICK,
        context: JSON.stringify({
            dealCategory,
            orderId,
        }),
    });
};

export const clickCancelBookingPopupCta = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId, dealCategory, action }: IClickCancelBookingPopupCta,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.CANCEL_BOOKING_POPUP,
        context: JSON.stringify({
            orderId,
            dealCategory,
            action,
        }),
    });
};

export const clickCallRestaurantCTA = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId, dealCategory }: IClickCallRestaurantCTA,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.CALL_RESTAURANT_CLICK,
        context: JSON.stringify({
            dealCategory,
            orderId,
        }),
    });
};

export const clickTnCDineoutBookingSuccess = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId, restaurantId, guests, dealCategory }: IClickTnCDineoutBookingSuccess,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.TNC_CLICK,
        context: JSON.stringify({
            orderId,
            restaurantId,
            guests,
            dealCategory,
        }),
    });
};

export const clickViewRestaurantDetails = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId }: IClickViewRestaurantDetails,
): void => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.VIEW_RESTAURANT_DETAILS_CLICK,
        context: JSON.stringify({
            orderId,
        }),
    });
};

export const clickBackButtonBookingDetails = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: DO_CLICK_ACTIONS.BACK_BUTTON,
        label: CLICK_LABELS.BACK_BUTTON,
    });
};

export const impressionBenefitCallout = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    isCheckoutFlow: boolean,
    { offerText }: IImpressionBenefitCallout,
): void => {
    impressionEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: DO_IMPRESSION_ACTIONS.BENEFIT_CALLOUT,
        label: IMPRESSION_LABELS.BOOKING_CARD,
        context: JSON.stringify({ offerText }),
    });
};

export const clickThingsToRemember = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    isCheckoutFlow: boolean,
    { orderId, restaurantId, guests, dealCategory }: IClickThingsToRemember,
) => {
    clickEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow ? DO_CLICK_ACTIONS.BOOKING_DETAILS : DO_CLICK_ACTIONS.DEAL_DETAILS,
        label: CLICK_LABELS.TOR_CLICK,
        context: JSON.stringify({ orderId, restaurantId, guests, dealCategory }),
    });
};
// eslint-disable-next-line max-lines
export const impressionYourBookings = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    isCheckoutFlow: boolean,
    { offer, orderId, dealCategory, restaurantId, city, oneStatus }: IImpressionYourBookings,
): void => {
    impressionEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: DO_IMPRESSION_ACTIONS.IMPRESSION_YOUR_BOOKINGS,
        label: IMPRESSION_LABELS.YOUR_BOOKINGS,
        context: JSON.stringify({
            offer,
            orderId,
            restaurantId,
            city,
            oneStatus,
            dealCategory,
        }),
    });
};

export const impressionBookingCard = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    isCheckoutFlow: boolean,
    { bookingStatus, orderId, dealCategory, flow }: IImpressionBookingCard,
): void => {
    impressionEvent({
        category: isCheckoutFlow ? SCREEN_NAME.BOOKING_SUCCESS : SCREEN_NAME.BOOKED_DEAL_DETAIL,
        action: isCheckoutFlow
            ? DO_IMPRESSION_ACTIONS.IMPRESSION_BOOKING_CARD
            : DO_IMPRESSION_ACTIONS.IMPRESSION_DEAL_DETAILS,
        label: IMPRESSION_LABELS.BOOKING_CARD,
        context: JSON.stringify({
            bookingStatus,
            orderId,
            dealCategory,
            flow,
        }),
    });
};
